import { defineStore } from 'pinia';
import type { ShippingMethod } from '~/repository/modules/ShippingMethod.module';
import type { PaymentMethod } from '~/repository/modules/payment.module';
import {
  CARD_PAYMENT_METHOD,
  PAYMENT_METHODS_THAT_ARE_MONTHLY_PAYMENT,
  SWISH_METHOD,
} from '~/models/checkout/PaymentMethods.model';
import type { ShoppingCart } from '~/models/shopping_cart/ShoppingCart.model';
import type {
  AppliedDiscount,
  NumberSelectionModel,
} from '~/models/shopping_cart/ShoppingCart.api.model';
import type { CustomerConsentDto } from '~/models/customer/CustomerConsent.api.model';
import { APP_CART_DATA_KEY } from '~/models/AsyncDataKeys.model';
import type { ShoppingCartSubTotalsModel } from '~/models/shopping_cart/ShoppingCartSubTotals.api.model';
import {
  defaultCheckoutIndicatorSteps,
  checkoutIndicatorStepsNoNumber,
  CHECKOUT_STEP_1_NAME,
} from '~/models/checkout/CheckoutSteps.model';
import type { StepIndicatorStep } from '~/components/StepIndicator.vue';
import { Category } from '~/models/Category.models';
import type { NumberConfigByIds } from '~/utils/checkout/checkoutStore/numberSelectionMap';
import type { ShoppingCartItemModel } from '~/models/shopping_cart/ShoppingCartItem.api.model';
import type { HalebopConfirmOrderRequest } from '~/models/checkout/ConfirmOrderRequest.api.model';

type CartProductPricesByItemIdAndDownPayment = {
  [key: string]: string;
};

type ShoppingCartDataState = {
  cartItems: ShoppingCartItemModel[];
  subTotals: ShoppingCartSubTotalsModel;
  appliedDiscounts: AppliedDiscount[];
  appliedDiscountsCouponCodes: string[];
};

export type FetchedData = {
  _teliaPayToken?: string;
  buybackIsEnabled?: boolean;
  selectedDownPayment?: number;
  cart: ShoppingCartDataState;
  confirmedOrderId?: number;
  numberConfigSections?: NumberSelectionModel[];
  ssn?: string;
  ssnCensored?: string;
  email?: string;
};

type PaymentState = {
  selectedPaymentMethod?: PaymentMethod;
  selectedDownPayTime: number;
  inputtedSwishPhoneNumber: string;
  initialDownpayTime: number;
};

export type UserState = {
  discountCode: string;
  numberConfigMap: NumberConfigByIds;
  newEmailAddress?: string;
  payment: PaymentState;
  selectedDeliveryMethod?: ShippingMethod;
  inputtedInstaboxPhoneNumber: string;
};

type UI = {
  bankIdTestPassthroughIsSubmitting: boolean;
  cartAccordionIsOpen: boolean;
  currentStep: number;
  customerRequiresAuth: boolean;
  loading: {
    isStep3AddressLoading: boolean;
    isStep3ShippingLoading: boolean;
    isStep3SsnCensoredLoading: boolean;
    isStep4TeliaPayInitiated: boolean;
  };
  numberConfigFormIsSubmitting: boolean;
  _steps: StepIndicatorStep[];
  teliaPayReady: boolean;
  _cartProductPricesByItemIdAndDownPayment: CartProductPricesByItemIdAndDownPayment;
};

type CheckoutStoreState = {
  customerConsents: CustomerConsentDto[];
  fetchedData: FetchedData;
  ui: UI;
  userState: UserState;
};

export type TrackingData = {
  ecommerce: {
    items: object[];
  };
  selectedShipping?: string;
  selectedPayment: string;
  numberOptions: object;
};

export const useMyCheckoutStore = defineStore({
  id: 'myCheckoutStore',
  state: (): CheckoutStoreState => ({
    customerConsents: [],
    fetchedData: {
      _teliaPayToken: '',
      buybackIsEnabled: true,
      cart: {
        cartItems: [],
        subTotals: {} as ShoppingCartSubTotalsModel,
        appliedDiscounts: [],
        appliedDiscountsCouponCodes: [],
      },
      confirmedOrderId: undefined,
      numberConfigSections: [],
      ssnCensored: '',
      selectedDownPayment: 36,
    },
    userState: {
      discountCode: '',
      numberConfigMap: {},
      newEmailAddress: undefined,
      payment: {
        selectedDownPayTime: 36,
        selectedPaymentMethod: undefined,
        inputtedSwishPhoneNumber: '',
        initialDownpayTime: 36,
      },
      selectedDeliveryMethod: undefined,
      inputtedInstaboxPhoneNumber: '',
    },
    ui: {
      bankIdTestPassthroughIsSubmitting: false,
      cartAccordionIsOpen: true,
      _cartProductPricesByItemIdAndDownPayment: {},
      currentStep: 1,
      customerRequiresAuth: true,
      loading: {
        isStep3AddressLoading: false,
        isStep3ShippingLoading: false,
        isStep3SsnCensoredLoading: false,
        isStep4TeliaPayInitiated: false,
      },
      numberConfigFormIsSubmitting: false,
      _steps: defaultCheckoutIndicatorSteps,
      teliaPayReady: false,
    },
  }),
  actions: {
    async fetchCart() {
      this.clearCartData();

      const { $api } = useNuxtApp();
      const shoppingCart =
        await $api.ShoppingCartModule.GetShoppingCart('ShoppingCart');
      if (shoppingCart) {
        this.populateCartState(shoppingCart);
      } else {
        this.clearCartData();
      }
      return shoppingCart;
    },
    async refreshCartAsyncDataHook() {
      refreshNuxtData(APP_CART_DATA_KEY);
    },
    async finalizeCheckout(): Promise<string> {
      const { $api } = useNuxtApp();

      const selectedPaymentMethodSystemName =
        this.userState.payment.selectedPaymentMethod
          ?.payment_method_system_name;

      const swishNumber =
        selectedPaymentMethodSystemName === SWISH_METHOD
          ? this.userState.payment.inputtedSwishPhoneNumber
          : undefined;

      if (!selectedPaymentMethodSystemName) {
        throw new Error();
      }

      await $api.PaymentModule.selectPaymentMethod(
        selectedPaymentMethodSystemName,
        swishNumber,
      );

      await $api.PaymentModule.getPaymentInfo();

      const confirmedOrder = await $api.checkout.confirmOrder(
        this.confirmOrderRequestPayload,
      );

      const orderId = confirmedOrder?.Id;
      const orderGuid = confirmedOrder?.OrderGuid;

      if (selectedPaymentMethodSystemName === CARD_PAYMENT_METHOD) {
        const paymentInfoWebViewResponse =
          await $api.PaymentModule.getPaymentInfoWebView(orderId);

        // Netaxept card payment
        if (
          checkoutStore_isPaymentInfoResponseEPaymentsRedirect(
            paymentInfoWebViewResponse,
          )
        ) {
          const cardPaymentRedirectUrl =
            checkoutStore_formatEPaymentRedirectUrl(paymentInfoWebViewResponse);

          if (cardPaymentRedirectUrl) {
            return cardPaymentRedirectUrl;
          }
        }
      }

      // Other payment methods redirect
      if (orderGuid) {
        return `/shop/kvitto/${orderGuid.replaceAll('-', '')}`;
      }

      return '';
    },
    populateCartState(shoppingCart: ShoppingCart) {
      this.fetchedData.cart = {
        appliedDiscounts: shoppingCart.AppliedDiscounts,
        appliedDiscountsCouponCodes: shoppingCart.DiscountCouponCodes,
        cartItems: shoppingCart.CartItems || [],
        subTotals: shoppingCart.SubTotals,
      };
      this.fetchedData.buybackIsEnabled = shoppingCart.EnableBuyback;
      this.fetchedData.selectedDownPayment = shoppingCart.SelectedDownPayments;
      this.userState.payment.selectedDownPayTime =
        shoppingCart.SelectedDownPayments;
      const lastOption =
        shoppingCart.SubTotals.Payments.findLast((x) => x.DownPayments !== 1)
          ?.DownPayments ?? 1;
      const downPayment =
        shoppingCart.SubTotals.Payments.find(
          (x) => x.DownPayments == shoppingCart.SelectedDownPayments,
        )?.DownPayments ?? 1;
      const downPaymentTime =
        lastOption < downPayment || downPayment === 1
          ? lastOption
          : downPayment;
      this.userState.payment.initialDownpayTime = downPaymentTime ?? 1;

      if (shoppingCart.NumberSelections) {
        this.fetchedData.numberConfigSections =
          shoppingCart.NumberSelections.slice();
      }

      this.ui.customerRequiresAuth = shoppingCart.CustomerRequiresAuth ?? true;
      const cartHasAnySubscriptions = shoppingCart.CartItems?.find(
        (item) =>
          item.Category === Category.postPaid ||
          item.Category === Category.prePaid ||
          !!item.BundlePlanItem?.DataAllowance?.DataSize,
      );

      this.ui._steps = cartHasAnySubscriptions
        ? defaultCheckoutIndicatorSteps
        : checkoutIndicatorStepsNoNumber;

      const newNumberConfigByBundleIdAndItemIdMap = checkoutStore_createMap(
        shoppingCart.NumberSelections,
        shoppingCart.CartItems,
      );

      this.userState.numberConfigMap = newNumberConfigByBundleIdAndItemIdMap;

      const newCartProductPricesByItemIdAndDownPayment: CartProductPricesByItemIdAndDownPayment =
        {};

      for (const item of shoppingCart.CartItems) {
        if (item.Payments) {
          for (const payment of item.Payments) {
            const key = checkoutStore_makeKeyForCartProductPriceMap(
              item.Id,
              payment.DownPayments,
            );

            let priceString: string = '';
            if (payment) {
              priceString = checkoutStore_makePriceStringForCartProductPriceMap(
                payment.ProductDownPayment.toLocaleString('sv-SE') ?? '0',
                payment.ProductPaymentUnit ?? 'kr',
              );
            }
            newCartProductPricesByItemIdAndDownPayment[key] = priceString;
          }
        }
      }
      this.ui._cartProductPricesByItemIdAndDownPayment =
        newCartProductPricesByItemIdAndDownPayment;
    },
    makeBuybackItemPriceStringToDisplay(item: ShoppingCartItemModel): string {
      const priceReduction = Array.isArray(item.PaymentReductions)
        ? item.PaymentReductions.find(
            (payment: {
              DownPayments: number;
              DownPaymentMonthlyReduction?: string;
            }) =>
              payment.DownPayments ===
              this.userState.payment.selectedDownPayTime,
          )?.DownPaymentMonthlyReduction
        : undefined;

      const dataReduction =
        (item.AdditionalDataAllowance?.DataSize ?? '') +
        (item.AdditionalDataAllowance?.DataUnit ?? '');

      return priceReduction
        ? `-${priceReduction} kr/mån`
        : `+${dataReduction}/mån`;
    },
    makeCartProductItemPriceStringToDisplay(itemId: number): string {
      const paymentMethodName =
        this.userState.payment.selectedPaymentMethod
          ?.payment_method_system_name;

      // Show monthly payment price string if no payment option not selected
      // (show montly payment by default)
      // Show direct payment if payment option selected and it is not a monthly payment method
      let shouldShowDirectPaymentPrice = false;

      if (paymentMethodName) {
        shouldShowDirectPaymentPrice =
          !PAYMENT_METHODS_THAT_ARE_MONTHLY_PAYMENT.includes(paymentMethodName);
      } else if (
        this.userState.payment.selectedDownPayTime &&
        this.userState.payment.selectedDownPayTime == 1
      ) {
        shouldShowDirectPaymentPrice = true;
      } else if (
        !this.fetchedData.cart.cartItems?.some(
          (x) => x.Category !== 'Tillbehör',
        )
      ) {
        shouldShowDirectPaymentPrice = true;
        this.userState.payment.selectedDownPayTime = 1;
      } else {
        shouldShowDirectPaymentPrice = false;
      }

      const downPayTime = shouldShowDirectPaymentPrice
        ? 1
        : this.userState.payment.selectedDownPayTime;

      const key = checkoutStore_makeKeyForCartProductPriceMap(
        itemId,
        downPayTime,
      );
      return this.ui._cartProductPricesByItemIdAndDownPayment[key] ?? '';
    },
    clearCartData() {
      this.fetchedData.cart = {
        appliedDiscounts: [],
        appliedDiscountsCouponCodes: [],
        cartItems: [],
        subTotals: {} as ShoppingCartSubTotalsModel,
      };
      this.ui._cartProductPricesByItemIdAndDownPayment = {};
      this.ui.currentStep = 1;
    },
    goToNextStep() {
      this.ui.currentStep = this.ui.currentStep + 1;
    },
  },
  getters: {
    cartContainsBundle: (state: CheckoutStoreState) => {
      return state.fetchedData.cart?.cartItems.some(
        (item: ShoppingCartItemModel) => item.BundleId,
      );
    },
    cartContainsCellphone: (state: CheckoutStoreState) => {
      return state.fetchedData.cart?.cartItems.some(
        (item: ShoppingCartItemModel) => item.Category === Category.phones,
      );
    },
    cartContainsSubsciptionsOnly: (state: CheckoutStoreState) => {
      return state.fetchedData.cart?.cartItems.reduce(
        (onlySubscription: boolean, item: ShoppingCartItemModel) =>
          onlySubscription &&
          (item.Category === Category.postPaid ||
            item.Category === Category.prePaid),
        true,
      );
    },
    confirmOrderRequestPayload: (
      state: CheckoutStoreState,
    ): HalebopConfirmOrderRequest =>
      checkoutStore_createConfirmOrderPayload(
        state.customerConsents,
        state.userState,
        state.fetchedData,
      ),
    currentCheckoutStepName: (state: CheckoutStoreState): string => {
      const zeroBasedArrayIndex = state.ui.currentStep - 1;
      const fallBackStepName =
        state.ui._steps.at(0)?.name || CHECKOUT_STEP_1_NAME;

      const stepName =
        state.ui._steps.at(zeroBasedArrayIndex)?.name || fallBackStepName;

      return stepName;
    },
    isCartEmpty: (state: CheckoutStoreState) => {
      return state.fetchedData.cart.cartItems.length === 0;
    },
    payment_directPaymentSumToDisplay: (state: CheckoutStoreState): number => {
      return state.fetchedData.cart?.subTotals?.SubTotalWithDiscount ?? 0;
    },
    payment_teliaPaySumToDisplay: (
      state: CheckoutStoreState,
    ): number | undefined => {
      // should calculation of combined price be a data point from the backend?

      const cartItems = state.fetchedData?.cart?.cartItems;
      if (!cartItems || !cartItems.length) {
        return 0;
      }

      const selectedDownPayTime = state.userState.payment.initialDownpayTime;
      const hardwareDownPayment =
        state.fetchedData.cart.subTotals.Payments.find(
          (payment) => payment.DownPayments === selectedDownPayTime,
        )?.HardwareMonthlyPayment;
      return hardwareDownPayment;
    },
    shippingMethodSelection: (state: CheckoutStoreState) => {
      return (
        state.userState.selectedDeliveryMethod?.Name +
        '___' +
        state.userState.selectedDeliveryMethod?.ShippingMethodSystemName
      );
    },
    steps: (state: CheckoutStoreState): StepIndicatorStep[] => {
      return state.ui._steps;
    },
    teliaPayToken: (state: CheckoutStoreState) => {
      return state.ui.teliaPayReady && state.fetchedData._teliaPayToken
        ? state.fetchedData._teliaPayToken
        : '';
    },
    trackingData(): TrackingData {
      const items = this.fetchedData.cart.cartItems.flatMap((item) =>
        tracking_mapCartItemModelToEcommerceItem(
          item,
          this.fetchedData.selectedDownPayment,
        ),
      );

      const trackingData: TrackingData = {
        ecommerce: {
          items: items,
        },
        selectedPayment:
          this.userState.payment.selectedPaymentMethod?.name ?? '',
        selectedShipping: this.userState.selectedDeliveryMethod?.Name,
        numberOptions: Object.keys(this.userState.numberConfigMap).map(
          (key) => {
            return {
              item_id: this.userState.numberConfigMap[key].cartItemId,
              e_sim: this.userState.numberConfigMap[key].esim,
              hidden_number: this.userState.numberConfigMap[key].hiddenNumber,
              number_selection:
                this.userState.numberConfigMap[key].keepNumberOption,
            };
          },
        ),
      };
      return trackingData;
    },
  },
});
